/// <amd-module name="Core/Medius.Core.Web/Scripts/Medius/apps/spaManager/router" />
import * as _ from "underscore";
import * as Backbone from "backbone";
import { isEmbedded, isPolarisEnabled } from "Core/Medius.Core.Web/Scripts/appConfig";
import * as featureToggle from "Core/Medius.Core.Web/Scripts/Medius/core/featureToggle";

// Domain migration configuration
const LEGACY_DOMAINS = ['cloud.mediusflow.com', 'email.mediusflow.com'];
const BIFROST_DOMAIN = 'app.medius.com';

// Old hash prefix → New path prefix
const REDIRECT_MAP: Record<string, string> = {
    '#/Tasks/ShowDocument': 'inbox/apa/ShowDocument',
    '#/Reports': 'reports'
};

// Hash prefixes that should NOT be redirected
const EXCLUDED_PREFIXES: string[] = [
    '#/procurement'
];

function shouldRedirectToNewDomain(): boolean {
    const onLegacyHost = LEGACY_DOMAINS.includes(window.location.hostname);
    const polarisEnabled = isPolarisEnabled();
    const embedded = isEmbedded();
    const featureEnabled = featureToggle.isRedirectToBifrostEnabled();
    console.log('[Polaris] shouldRedirectToNewDomain check:', { onLegacyHost, polarisEnabled, embedded, featureEnabled });
    return onLegacyHost && polarisEnabled && !embedded && featureEnabled;
}

function getTenantFromPath(): string {
    return window.location.pathname.split('/')[1] || '';
}

function tryRedirectToNewDomain(): boolean {
    if (!shouldRedirectToNewDomain()) {
        return false;
    }

    const tenant = getTenantFromPath();
    if (!tenant) {
        return false;
    }

    const hash = window.location.hash;

    // Check for excluded prefixes (e.g., procurement URLs)
    for (const excludedPrefix of EXCLUDED_PREFIXES) {
        if (hash.startsWith(excludedPrefix)) {
            console.log(`[Polaris] Skipping redirect for excluded URL: ${hash}`);
            return false;
        }
    }

    // No hash or just root - redirect to tenant root
    if (!hash || hash === '#' || hash === '#/' || hash === '#/Home') {
        const newUrl = `https://${BIFROST_DOMAIN}/${tenant}`;
        console.log(`[Polaris] Redirecting from ${window.location.href} to ${newUrl}`);
        window.location.replace(newUrl);
        return true;
    }

    // Check for specific hash mappings
    for (const [oldPrefix, newPrefix] of Object.entries(REDIRECT_MAP)) {
        if (hash.startsWith(oldPrefix)) {
            const remainder = hash.slice(oldPrefix.length); // e.g., "/209"
            const newUrl = `https://${BIFROST_DOMAIN}/${tenant}/${newPrefix}${remainder}`;
            console.log(`[Polaris] Redirecting from ${window.location.href} to ${newUrl}`);
            window.location.replace(newUrl);
            return true;
        }
    }

    // Fallback: redirect to new-experience with legacy URL as parameter
    const legacyUrl = encodeURIComponent(window.location.href);
    const newUrl = `https://${BIFROST_DOMAIN}/${tenant}/new-experience?legacyUrl=${legacyUrl}`;
    console.log(`[Polaris] Fallback redirecting from ${window.location.href} to ${newUrl}`);
    window.location.replace(newUrl);
    return true;
}

const Router = Backbone.Router.extend({

    initialize: function (spaManager: any) {
        // Check for redirect before setting up routes
        if (tryRedirectToNewDomain()) {
            return;
        }

        _(spaManager.Apps).each((app) => {
            _(app.routes).each((route) => {
                this.route(route.path, route.path, function () {
                    if (_.isFunction(route.action)) {
                        route.action.apply(app, arguments);
                    }
                });
            });
        });
    },

    updateUrl: function (elements: any, trigger: any, replace: any) {
        const url = _.isArray(elements)
            ? elements.join("/")
            : elements;


        this.navigate(url, {
            trigger: trigger || false,
            replace: false || replace
        });

        return true;
    },

    navigateTo: function (elements: any) {
        this.updateUrl(elements, true);
    },

    navigateToInNewTab: function (route: any) {
        window.open(window.location.href.split("#")[0] + route, '_blank');
    },

    openLinkInNewTab: function (link: any) {
        window.open(link, '_blank');
    },
});

export function create(spaManager: any) {
    return new Router(spaManager);
}
